import React, {memo, useEffect, useMemo, useRef, useState} from 'react';
import type {LngLatBounds} from 'mapbox-gl';
import {BalloonEventModel, PartnerModel} from 'ts-core';
import {EventTilesList} from './EventTilesList';
import {EventItemsList} from './EventItemsList';
import {ViewMode, ViewToggleSwitch} from './ViewToggleSwitch';
import {Spinner, trackMixpanel, useIsMobile, useQuery, useTheme, useTranslations} from "react-ui";
import {EventsListProvider, useEventsListContext} from '../contexts/EventsListContext';
import {useInfiniteScroll} from "../hooks/useInfiniteScroll.ts";
import {useBottomSheetContext} from "../hooks/useBottomSheet.ts";
import {useMapBox} from './BalloonEventsMapBox';
import {useBalloonEventsData} from '../contexts/EventData';
import {useUserScope} from '../contexts/UserScope';
import {usePartnerScope} from '../contexts/PartnerScope';

export interface EventsListProps {
    events: BalloonEventModel[];
    onLoadMore: () => void;
    hasMore: boolean;
    isLoading?: boolean;
    defaultView?: ViewMode;
    persistViewPreference?: boolean;
    partners: PartnerModel[];
}

const STORAGE_KEY = 'eventsViewPreference';

/**
 * Restrict the list to events inside the map's current viewport so the sidebar
 * mirrors what's actually on the map. The `map` search-query bbox is widened by
 * 40% and isn't updated when the user zooms in within that buffer (a clustering
 * stability optimization in BalloonEventsMapBox), so `query.fits` alone leaves
 * the list showing events well outside the visible area. We re-filter against
 * the live `map.getBounds()` — the same bounds the canvas clusters against —
 * and re-run on every `moveend`. Falls back to the unfiltered list until the
 * map and its bounds are available.
 */
function useViewportEvents(events: BalloonEventModel[]): BalloonEventModel[] {
    const mapBoxInfo = useMapBox();
    const map = mapBoxInfo?.map;
    const [bounds, setBounds] = useState<LngLatBounds | null>(null);

    useEffect(() => {
        if (!map) return;
        const update = () => setBounds(map.getBounds());
        update();
        map.on('moveend', update);
        return () => {
            map.off('moveend', update);
        };
    }, [map]);

    return useMemo(() => {
        if (!bounds) return events;
        return events.filter((e) => {
            const lat = e.location?.lat;
            const lng = e.location?.lng;
            if (lat == null || lng == null || !isFinite(lat) || !isFinite(lng)) return false;
            return bounds.contains([lng, lat]);
        });
    }, [events, bounds]);
}

/**
 * Freeze the visible order while a detail panel is open. Opening an event
 * triggers `ensureLoad`, which mutates the event in the repo and re-runs the
 * sort/quadrant bucketing — making the tile jump position underneath the
 * panel. We snapshot the id order on the render where `openedEvent` flips
 * non-null and keep known events in their original slots until it closes.
 * New events arriving during the freeze go to the end; removed events drop
 * out.
 */
function useStableOrderWhileOpened(events: BalloonEventModel[]): BalloonEventModel[] {
    const { openedEvent } = useBalloonEventsData();
    const isFrozen = openedEvent != null;
    const frozenOrderRef = useRef<string[] | null>(null);

    useEffect(() => {
        if (!isFrozen) {
            frozenOrderRef.current = null;
        }
    }, [isFrozen]);

    return useMemo(() => {
        if (!isFrozen) return events;
        if (frozenOrderRef.current == null) {
            frozenOrderRef.current = events.map((e) => e.id);
        }
        const known = frozenOrderRef.current;
        const byId = new Map(events.map((e) => [e.id, e] as const));
        const out: BalloonEventModel[] = [];
        for (const id of known) {
            const e = byId.get(id);
            if (e) {
                out.push(e);
                byId.delete(id);
            }
        }
        for (const e of byId.values()) out.push(e);
        return out;
    }, [events, isFrozen]);
}

export const EventsList: React.FC<EventsListProps> = memo(function EventsList({
    events: allEvents,
    onLoadMore,
    hasMore,
    isLoading = false,
    defaultView = 'tiles',
    persistViewPreference = true,
}) {
    const {translated} = useTranslations();
    const {slug: scopedSlug, profile: scopedProfile} = useUserScope();
    const {code: scopedPartnerCode, profile: scopedPartnerProfile, hasEvents: partnerHasEvents} = usePartnerScope();
    const scopedName = scopedSlug
        ? (scopedProfile?.name ?? scopedSlug)
        : scopedPartnerCode && partnerHasEvents
            ? (scopedPartnerProfile?.name ?? scopedPartnerCode)
            : null;
    const isMobile = useIsMobile();
    const bottomSheetContext = useBottomSheetContext();
    const viewportEvents = useViewportEvents(allEvents);
    const events = useStableOrderWhileOpened(viewportEvents);

    // Desktop: the user toggles tiles/list via the header switch (persisted).
    const [manualView, setManualView] = useState<ViewMode>(() => {
        if (persistViewPreference && typeof window !== 'undefined') {
            const saved = localStorage.getItem(STORAGE_KEY) as ViewMode | null;
            return saved || defaultView;
        }
        return defaultView;
    });

    const handleViewChange = (newView: ViewMode) => {
        setManualView(newView);
        if (persistViewPreference && typeof window !== 'undefined') {
            localStorage.setItem(STORAGE_KEY, newView);
        }
    };

    // Mobile: there is no toggle — the view is driven by the bottom-sheet state.
    // half/collapsed → tiles carousel; expanded → vertical list. Expanding the
    // sheet therefore transitions the carousel into the list automatically.
    const view: ViewMode = isMobile
        ? (bottomSheetContext?.state === 'expanded' ? 'list' : 'tiles')
        : manualView;

    // Track whether a fetch has actually completed at least once. `isLoading`
    // alone is unreliable on first render: the context's `loading` flag is
    // derived from `callcount > 0`, which is still 0 in the tick before the
    // first fetch dispatches — so `!isLoading && events.length === 0` flashes
    // true and would auto-expand the sheet before any data is even requested.
    const [hasSettled, setHasSettled] = useState(false);
    const wasLoadingRef = useRef(false);
    useEffect(() => {
        if (wasLoadingRef.current && !isLoading) {
            setHasSettled(true);
        }
        wasLoadingRef.current = isLoading;
    }, [isLoading]);

    const showLoadingState = !hasSettled || (isLoading && events.length === 0);
    const isEmpty = hasSettled && !isLoading && events.length === 0;
    // Header (heading + view toggle) is desktop-only; on mobile the bottom-sheet
    // state drives the view, so there is no heading or toggle.
    const showHeader = !showLoadingState && !isEmpty && !isMobile;

    return (
        <EventsListProvider>
            <div className="relative events-list-container w-full h-full overflow-hidden flex-col flex-1">
                {showHeader && (
                    <div className="events-list-header w-full flex flex-row items-center justify-between gap-3 px-4 py-2 pb-0 md:py-4">
                        <h2 className="text-balloon text-2xl font-medium font-circular leading-tight">
                            {scopedName
                                ? `${translated('events_by') || 'Events by'} ${scopedName}`
                                : translated('events_near_you') || 'Events near you'}
                        </h2>
                        <ViewToggleSwitch view={view} onChange={handleViewChange} />
                    </div>
                )}

                <EventsListContent
                    view={view}
                    events={events}
                    onLoadMore={onLoadMore}
                    hasMore={hasMore}
                    isLoading={isLoading}
                    showLoadingState={showLoadingState}
                    isEmpty={isEmpty}
                />
            </div>
        </EventsListProvider>
    );
});

// Separate component to access context
const EventsListContent: React.FC<{
    view: ViewMode;
    events: BalloonEventModel[];
    onLoadMore: () => void;
    hasMore: boolean;
    isLoading: boolean;
    showLoadingState: boolean;
    isEmpty: boolean;
}> = memo(function EventsListContent({ view, events, onLoadMore, hasMore, isLoading, showLoadingState, isEmpty }) {
    const { listId, containerRef } = useEventsListContext();
    const isMobile = useIsMobile();
    const className = useHorizontalList(view, isMobile);
    const { primaryColor } = useTheme();
    const { translated } = useTranslations();
    const mapBoxInfo = useMapBox();
    const map = mapBoxInfo?.map;

    const bottomSheetContext = useBottomSheetContext();
    const {has, removeQueryById, clear, queries} = useQuery();
    const hasDatesFilter = has('dates');
    const filterCount = queries.length;

    // Empty state has nothing meaningful at full height, so disable the
    // `expanded` snap point. Collapse and half remain available so the user
    // can still hide the sheet entirely if they want to see the map. The hook
    // auto-corrects if the sheet is already expanded when this flips on.
    useEffect(() => {
        if (!isMobile || !bottomSheetContext) return;
        bottomSheetContext.setDisallowExpanded(isEmpty);
    }, [isEmpty, isMobile, bottomSheetContext]);

    const currentZoom = map ? map.getZoom() : null;

    const handleZoomOut = () => {
        if (!map) return;
        trackMixpanel('EmptyState', 'ZoomOut:Click', {currentZoom, filterCount});
        map.flyTo({ zoom: map.getZoom() - 2, duration: 600 });
    };

    const handleExpandTimeframe = () => {
        if (!hasDatesFilter) return;
        trackMixpanel('EmptyState', 'ExpandTimeframe:Click', {currentZoom, filterCount});
        removeQueryById('dates');
    };

    const handleClearFilters = () => {
        if (filterCount === 0) return;
        trackMixpanel('EmptyState', 'ClearFilters:Click', {currentZoom, filterCount});
        clear();
    };

    if (showLoadingState) {
        return (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
                <Spinner/>
                <p className="text-sm text-gray-500">
                    {translated('loading') || 'Loading...'}
                </p>
            </div>
        );
    }

    if (isEmpty) {
        const actionCls =
            'w-full rounded-full px-6 py-3 text-base font-medium text-white transition-opacity hover:opacity-80 active:opacity-70 disabled:opacity-40 disabled:cursor-not-allowed';
        const isHalf = bottomSheetContext?.state === 'half';
        const verticalAlignCls = isHalf ? 'justify-start pt-4' : 'justify-center';
        return (
            <div className={`absolute inset-0 flex flex-col items-center gap-3 px-6 text-center ${verticalAlignCls}`}>
                <h3 className="text-base font-semibold text-gray-800">
                    {translated('empty_state_title') || 'No events match this view'}
                </h3>
                <p className="text-sm text-gray-500">
                    {translated('empty_state_subtitle') || 'Try one of these:'}
                </p>
                <div className="flex flex-col items-stretch gap-2 w-full max-w-sm">
                    <button
                        onClick={handleZoomOut}
                        className={actionCls}
                        style={{backgroundColor: primaryColor}}
                    >
                        {translated('zoom_out') || 'Zoom out'}
                    </button>
                    {hasDatesFilter && (
                        <button
                            onClick={handleExpandTimeframe}
                            className={actionCls}
                            style={{backgroundColor: primaryColor}}
                        >
                            {translated('expand_timeframe') || 'Expand timeframe'}
                        </button>
                    )}
                    {filterCount > 0 && (
                        <button
                            onClick={handleClearFilters}
                            className={actionCls}
                            style={{backgroundColor: primaryColor}}
                        >
                            {translated('clear_filters') || 'Clear filters'}
                        </button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div ref={containerRef} id={listId} className={className}>
            {view === 'tiles' ? (
                <EventTilesList
                    events={events}
                    onLoadMore={onLoadMore}
                    hasMore={hasMore}
                    isLoading={isLoading}
                />
            ) : (
                <EventItemsList
                    events={events}
                    onLoadMore={onLoadMore}
                    hasMore={hasMore}
                    isLoading={isLoading}
                />
            )}
        </div>
    );
});
function useHorizontalList(viewType: ViewMode, isMobile: boolean) {
    const bottomSheetContext = useBottomSheetContext();
    // The header (heading + toggle) is removed on mobile, so the scroll area
    // starts at the very top instead of below the desktop header.
    const topClass = isMobile ? "top-0" : "top-[50px]";
    const verticalClass =
        `event-list-content absolute bottom-0 left-0 right-0 ${topClass} events-list-content vertical w-full overflow-y-auto flex-1`;

    if (viewType !== "tiles" || !bottomSheetContext) {
        return verticalClass;
    }
    if (bottomSheetContext.state === "half") {
        return `event-list-content absolute bottom-0 left-0 right-0 ${topClass} events-list-content horizontal overflow-x-auto flex-1`;
    }
    // Tiles + expanded (or any non-half state): the inner layout switches to
    // a 2-col grid in EventTilesList, so the outer container needs vertical
    // scroll. Falling through to `undefined` here disables scrolling entirely.
    return verticalClass;
}